
import { useState, useEffect } from "react";
import { getPlaceholderImage, getServiceIcon } from "@/utils/imageUtils";

interface ContractorImageProps {
  imageUrl: string;
  name: string;
  serviceType: string;
  city: string;
}

export const ContractorImage = ({ imageUrl, name, serviceType, city }: ContractorImageProps) => {
  const [imgError, setImgError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Make sure we have a valid URL or fall back to a placeholder
  const finalImageUrl = imgError || !imageUrl ? getPlaceholderImage(serviceType) : imageUrl;
  
  // Create a descriptive alt text
  const altText = `${name} - ${serviceType} in ${city}, OK`;
  
  console.log('ContractorImage rendering with:', {
    name,
    serviceType,
    providedImageUrl: imageUrl,
    finalImageUrl,
    hasError: imgError
  });

  // Preload image to check if it's valid
  useEffect(() => {
    if (!imageUrl) {
      setImgError(true);
      setIsLoading(false);
      return;
    }

    const img = new Image();
    img.onload = () => {
      setImgError(false);
      setIsLoading(false);
    };
    img.onerror = () => {
      console.warn(`Image failed to preload for ${name}:`, imageUrl);
      setImgError(true);
      setIsLoading(false);
    };
    img.src = imageUrl;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, name]);

  // Get the icon component for fallback
  const IconComponent = getServiceIcon(serviceType);

  return (
    <div className="bg-gradient-to-br from-[#918FB6] via-[#919191] to-[#BFBFBF] p-6 rounded-lg shadow-sm">
      <div className="relative aspect-video w-full flex items-center justify-center">
        {isLoading ? (
          <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        ) : imgError && IconComponent ? (
          <div className="flex flex-col items-center justify-center">
            <IconComponent className="w-20 h-20 text-white mb-2" />
            <span className="text-white font-medium">{serviceType}</span>
          </div>
        ) : (
          <img
            src={finalImageUrl}
            alt={altText}
            className="w-auto h-auto min-w-[50%] min-h-[50%] max-w-[90%] max-h-[90%] object-contain"
            onError={(e) => {
              console.warn(`Image failed to load for ${name}:`, finalImageUrl);
              setImgError(true);
              // Don't set src here as it might cause an infinite loop
              // Instead, the component will re-render with the icon fallback
            }}
          />
        )}
      </div>
    </div>
  );
};
